import { FileTextIcon, TagIcon } from '@phosphor-icons/react';
import type { IconProps } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import DataTable, { type Action as TableAction } from 'src/components/ui/Table';
import moment from 'moment';

interface TagStyle {
  color: string;
  icon: React.ReactNode;
}

interface TagEntry {
  name: string;
  style?: TagStyle;
}

interface Document {
  id: string;
  title: string;
  tags: (string | TagEntry)[];
  lastViewed: string;
  published: string;
  icon?: React.ReactNode;
}

type Action = TableAction<Document>;

interface DocumentTableProps {
  documents: Document[];
  onRowClick: (doc: Document) => void;
  icon?: React.ForwardRefExoticComponent<IconProps>;
  placeholder?: string;
  columns?: any[];
  actions?: Action[];
  error?: string | null;
}

export default function DocumentTable({
  documents,
  onRowClick,
  icon: IconComponent = FileTextIcon,
  columns: customColumns,
  actions,
  error
}: DocumentTableProps) {
  const [searchQuery] = useState('');
  const [filteredDocuments, setFilteredDocuments] = useState(
    documents.map((doc) => ({
      ...doc,
      icon: doc.icon || <IconComponent size={20} weight="light" />
    }))
  );

  useEffect(() => {
    if (!searchQuery) {
      setFilteredDocuments(
        documents.map((doc) => ({
          ...doc,
          icon: doc.icon || <IconComponent size={20} weight="light" />
        }))
      );
      return;
    }

    const searchLower = searchQuery.toLowerCase();
    const filtered = documents.filter((doc) => {
      return ['title', 'tags', 'lastViewed'].some((key) => {
        const value = doc[key as keyof Document];
        if (Array.isArray(value)) {
          return value.some((tag) => {
            if (typeof tag === 'string') {
              return tag.toLowerCase().includes(searchLower);
            } else {
              return tag.name.toLowerCase().includes(searchLower);
            }
          });
        }
        return value && String(value).toLowerCase().includes(searchLower);
      });
    });
    setFilteredDocuments(
      filtered.map((doc) => ({
        ...doc,
        icon: doc.icon || <IconComponent size={20} weight="light" />
      }))
    );
  }, [searchQuery, documents, IconComponent]);

  if (!customColumns) {
    const defaultColumns = [
      {
        key: 'title',
        header: 'Title'
      },
      {
        key: 'tags',
        header: 'Tags',
        render: (doc: Document) => (
          <div className="flex flex-wrap gap-2">
            {doc.tags?.map((tag, index) => {
              const tagName = typeof tag === 'string' ? tag : tag.name;
              const tagStyle = typeof tag === 'string' ? undefined : tag.style;

              return (
                <span
                  key={`${tagName}-${index}`}
                  className={`inline-flex items-center ${tagStyle?.color || 'bg-gray-100 text-gray-800 border-gray-200'} text-sm font-normal px-3 py-1 rounded-md`}
                >
                  {tagStyle?.icon || <TagIcon size={16} weight="light" className="mr-2" />}
                  {tagName}
                </span>
              );
            })}
          </div>
        )
      },
      {
        key: 'lastViewed',
        header: 'Last Viewed'
      },
      {
        key: 'published',
        header: 'Published',
        render: (doc: Document) => (doc.published ? moment(doc.published).format('DD/MM/YYYY') : '')
      }
    ];
    customColumns = defaultColumns;
  }

  return (
    <DataTable
      data={filteredDocuments}
      columns={customColumns}
      itemsPerPage={5}
      actions={actions}
      onRowClick={onRowClick}
      error={error}
      enableSorting={false}
    />
  );
}

export type { Document, Action };
